'use client'

import { useCallback, PropsWithChildren, createContext, useContext, useState } from 'react'
import { useApi } from '@/contexts/ApiContext'
import {
    HealthHistory,
    Allergy,
    Condition,
    Procedure,
    Medication,
    MetriportHealthHistory,
} from '@/types/interfaces/health-history'
import { GenericChartItem } from '@/types/interfaces/health-history'
import { useLocalStorage } from '@mantine/hooks'
import { useRouter } from 'next/navigation'

// The order of this array determines the order of the flow
export const HEALTH_HISTORY_STEPS = ['conditions', 'medications', 'allergies', 'procedures'] as const
export type HealthHistoryStep = (typeof HEALTH_HISTORY_STEPS)[number]

const getNextOrderedStep = (step: HealthHistoryStep) => {
    const index = HEALTH_HISTORY_STEPS.indexOf(step)
    if (index + 1 < HEALTH_HISTORY_STEPS.length) {
        return HEALTH_HISTORY_STEPS[index + 1]
    }
}

export const emptyHealthHistory: HealthHistory = {
    conditions: [],
    medications: [],
    allergies: [],
    procedures: [],
    no_conditions: false,
    no_medications: false,
    no_allergies: false,
    no_procedures: false,
}

interface HealthHistoryContextData {
    includedInItems: (itemName: string, itemSource: string, items: GenericChartItem[]) => boolean
    removeItem: (category: string, itemName: string, itemSource: string, items: GenericChartItem[]) => void
    addOrRemoveItem: (category: string, item: GenericChartItem, items: GenericChartItem[]) => void
    addOrRemoveFreeTextItem: (category: string, query: string, items: GenericChartItem[]) => void
    /** Drops the category's chosen items that have not been saved yet, keeping those that already have an id. */
    discardUnsavedItems: (category: string) => void
    loadingHealthHistory: boolean
    latestMetriportHealthHistory: () => Promise<MetriportHealthHistory>
    checkHealthHistoryExists: () => void
    fetchHealthHistory: (forceReload?: boolean) => Promise<HealthHistory | null | undefined>
    particleHealthHistoryToReview: boolean
    baseRoute: string
    canContinue: () => boolean
    routeOnStepCompletion: (currentStep: HealthHistoryStep, latestData?: HealthHistory) => void
    routeOnInit: (options: { baseRoute: string }) => void
    healthHistoryData: HealthHistory
    allergies: Allergy[]
    conditions: Condition[]
    procedures: Procedure[]
    medications: Medication[]
    updateHealthHistory: (category: string, data: any) => Promise<HealthHistory>
    sendMetriportReviewToCopilot: () => Promise<void>
}

const HealthHistoryContext = createContext<HealthHistoryContextData>({
    includedInItems: () => false,
    removeItem: () => undefined,
    addOrRemoveItem: () => undefined,
    addOrRemoveFreeTextItem: () => undefined,
    discardUnsavedItems: () => undefined,
    loadingHealthHistory: false,
    latestMetriportHealthHistory: () => Promise.resolve({} as MetriportHealthHistory),
    checkHealthHistoryExists: () => undefined,
    fetchHealthHistory: async (forceReload: boolean = false) => null,
    healthHistoryData: emptyHealthHistory,
    particleHealthHistoryToReview: false,
    baseRoute: '/health-history',
    canContinue: () => false,
    routeOnStepCompletion: () => undefined,
    routeOnInit: () => undefined,
    allergies: [],
    conditions: [],
    procedures: [],
    medications: [],
    updateHealthHistory: () => Promise.resolve(emptyHealthHistory),
    sendMetriportReviewToCopilot: () => Promise.resolve(),
})

const STORAGE_KEYS = {
    BASE_ROUTE: 'hh_base',
} as const

export default function HealthHistoryProvider({ children }: PropsWithChildren) {
    const { apiGet } = useApi()
    const router = useRouter()
    const [healthHistoryData, setHealthHistoryData] = useState<HealthHistory>(emptyHealthHistory)
    const [particleHealthHistoryToReview, setParticleHealthHistoryToReview] = useState(false)
    const [fetchedHealthHistory, setFetchedHealthHistory] = useState(false)
    const [loadingHealthHistory, setLoadingHealthHistory] = useState(false)
    const { apiPost } = useApi()

    // e.g. /health-history or /visit/123/health-history
    const [baseRoute, setBaseRoute] = useLocalStorage<string>({
        key: STORAGE_KEYS.BASE_ROUTE,
        defaultValue: '/health-history',
    })

    const includedInItems = (itemName: string, itemSource: string, items: GenericChartItem[]) => {
        for (let i of items) {
            if (i.name && i.name === itemName && i.source && i.source == itemSource) {
                return true
            }
        }
        return false
    }

    const setItems = (category: string, items: GenericChartItem[]) => {
        switch (category.toLowerCase()) {
            case 'allergies':
                setHealthHistoryData({ ...healthHistoryData, allergies: items })
                break
            case 'conditions':
                setHealthHistoryData({ ...healthHistoryData, conditions: items })
                break
            case 'procedures':
                setHealthHistoryData({ ...healthHistoryData, procedures: items })
                break
            case 'medications':
                setHealthHistoryData({ ...healthHistoryData, medications: items })
                break
        }
    }

    const addOrRemoveItem = (category: string, item: GenericChartItem, items: GenericChartItem[]) => {
        if (includedInItems(item.name, item.source, items)) {
            removeItem(category, item.name, item.source, items)
        } else {
            addItem(category, item, items)
        }
    }

    const addItem = (category: string, item: GenericChartItem, items: GenericChartItem[]) => {
        if (includedInItems(item.name, item.source, items)) {
            return
        }

        const itemListCopy = [...items]
        itemListCopy.push(item)
        setItems(category, itemListCopy)
    }

    const addFreeTextItem = (category: string, itemName: string, items: GenericChartItem[]) => {
        const newItem: GenericChartItem = {
            name: itemName,
            source: 'customer',
            id: '',
            source_id: '',
            display_name: itemName,
        }

        const itemListCopy = [...items]
        itemListCopy.push(newItem)
        setItems(category, itemListCopy)
    }

    const addOrRemoveFreeTextItem = (category: string, itemName: string, items: GenericChartItem[]) => {
        if (includedInItems(itemName, 'customer', items)) {
            removeItem(category, itemName, 'customer', items)
        } else {
            addFreeTextItem(category, itemName, items)
        }
    }

    const removeItem = (category: string, itemName: string, itemSource: string, items: GenericChartItem[]) => {
        for (var i = 0; i < items.length; i++) {
            if (itemName && items[i].name === itemName && items[i].source === itemSource) {
                const itemListCopy = [...items]
                itemListCopy.splice(i, 1)
                setItems(category, itemListCopy)
            }
        }
    }

    const discardUnsavedItems = (category: string) => {
        const items = healthHistoryData[category.toLowerCase() as keyof HealthHistory]
        if (!Array.isArray(items) || items.every((item: GenericChartItem) => item.id)) return
        setItems(category, items.filter((item: GenericChartItem) => item.id))
    }

    const checkHealthHistoryExists = async () => {
        try {
            const data = await apiGet('/external/health_history/lookup_exists')
            setParticleHealthHistoryToReview(data.lookup_exists)
        } catch (err) {
            console.error('Error checking health history lookup exists: ', err)
        }
    }

    const latestMetriportHealthHistory = useCallback(async () => {
        const data = await apiGet<MetriportHealthHistory>('/external/health_history/latest_metriport')
        return data
    }, [apiGet])

    const updateHealthHistory = async (category: string, data: any) => {
        const response = await apiPost<HealthHistory>(`/external/manual_health_history/update_${category}`, data)
        setHealthHistoryData(response)
        return response
    }

    const sendMetriportReviewToCopilot = useCallback(async () => {
        try {
            await apiPost('/external/health_history/send_metriport_review_to_copilot')
        } catch (err) {
            console.error('Error sending metriport review to copilot: ', err)
            throw err
        }
    }, [apiPost])

    const fetchHealthHistory = useCallback(
        async (forceReload = false) => {
            if (loadingHealthHistory) return
            if (!forceReload && fetchedHealthHistory) {
                return
            }

            setLoadingHealthHistory(true)

            let data: HealthHistory = emptyHealthHistory
            try {
                data = await apiGet<HealthHistory>('/external/health_history/show')
                setHealthHistoryData(data)
                setFetchedHealthHistory(true)
            } catch (err) {
                console.error('Error fetching health history: ', err)
                setFetchedHealthHistory(true)
                setHealthHistoryData(emptyHealthHistory)
            }

            setLoadingHealthHistory(false)
            return data
        },
        [fetchedHealthHistory, apiGet, loadingHealthHistory],
    )

    const allDataPresent = (data: HealthHistory | undefined): boolean =>
        !!data &&
        (data.conditions.length > 0 || data.no_conditions) &&
        (data.medications.length > 0 || data.no_medications) &&
        (data.allergies.length > 0 || data.no_allergies) &&
        (data.procedures.length > 0 || data.no_procedures)

    const canContinue = () => allDataPresent(healthHistoryData)

    const routeOnInit = async (options: { baseRoute: string } = { baseRoute: baseRoute }) => {
        const newBaseRoute = options.baseRoute
        setBaseRoute(newBaseRoute)
        const data = await fetchHealthHistory(true)
        if (allDataPresent(data)) router.push(newBaseRoute)
        else router.push(`${newBaseRoute}/${HEALTH_HISTORY_STEPS[0]}/add`)
    }

    const routeOnStepCompletion = async (currentStep: HealthHistoryStep, latestData?: HealthHistory) => {
        const dataToCheck = latestData || healthHistoryData
        if (!allDataPresent(dataToCheck)) {
            const nextOrderedStep = getNextOrderedStep(currentStep)
            if (nextOrderedStep) {
                return router.push(`${baseRoute}/${nextOrderedStep}/add`)
            }
        } else {
            router.push(baseRoute)
        }
    }

    return (
        <HealthHistoryContext.Provider
            value={{
                includedInItems,
                removeItem,
                addOrRemoveItem,
                addOrRemoveFreeTextItem,
                discardUnsavedItems,
                loadingHealthHistory,
                latestMetriportHealthHistory,
                checkHealthHistoryExists,
                fetchHealthHistory,
                healthHistoryData,
                allergies: healthHistoryData.allergies,
                conditions: healthHistoryData.conditions,
                procedures: healthHistoryData.procedures,
                medications: healthHistoryData.medications,
                particleHealthHistoryToReview,
                canContinue,
                baseRoute,
                routeOnInit,
                routeOnStepCompletion,
                updateHealthHistory,
                sendMetriportReviewToCopilot,
            }}
        >
            {children}
        </HealthHistoryContext.Provider>
    )
}

export const useHealthHistoryContext = () => {
    return useContext(HealthHistoryContext)
}
